/**
 * Users Service
 * 
 * Handles user profile management and lookup operations.
 * User profiles are stored in Firestore to enable email-based lookups
 * for member invitations.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../firebase'

// Collection name
export const USERS_COLLECTION = 'users'

/**
 * Ensure a user profile exists in Firestore
 * Called after authentication to sync Firebase Auth user with Firestore
 * 
 * @param {object} user - Firebase Auth user object
 * @returns {Promise<object>} - The user profile document
 */
export async function ensureUserProfile(user) {
  if (!user?.uid || !user?.email) {
    throw new Error('User with uid and email is required')
  }

  const userRef = doc(db, USERS_COLLECTION, user.uid)
  const userSnap = await getDoc(userRef)

  if (userSnap.exists()) {
    // Update last login time
    await setDoc(userRef, {
      lastLoginAt: serverTimestamp(),
      // Update email in case it changed
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0],
    }, { merge: true })

    return { id: userSnap.id, ...userSnap.data() }
  }

  // Create new user profile
  const newProfile = {
    email: user.email,
    displayName: user.displayName || user.email.split('@')[0],
    photoURL: user.photoURL || null,
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
  }

  await setDoc(userRef, newProfile)

  return { id: user.uid, ...newProfile }
}

/**
 * Get a user profile by ID
 * 
 * @param {string} userId - The user's UID
 * @returns {Promise<object|null>} - The user profile or null if not found
 */
export async function getUserProfile(userId) {
  if (!userId) {
    throw new Error('User ID is required')
  }

  const userRef = doc(db, USERS_COLLECTION, userId)
  const userSnap = await getDoc(userRef)

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() }
  }

  return null
}

/**
 * Find a user by email address
 * 
 * @param {string} email - The email to search for
 * @returns {Promise<object|null>} - The user profile or null if not found
 */
export async function getUserByEmail(email) {
  if (!email) {
    throw new Error('Email is required')
  }

  const normalizedEmail = email.toLowerCase().trim()
  
  const usersRef = collection(db, USERS_COLLECTION)
  const q = query(
    usersRef,
    where('email', '==', normalizedEmail),
    limit(1)
  )

  const querySnap = await getDocs(q)

  if (querySnap.empty) {
    return null
  }

  const userDoc = querySnap.docs[0]
  return { id: userDoc.id, ...userDoc.data() }
}

/**
 * Get multiple user profiles by IDs
 * 
 * @param {string[]} userIds - Array of user IDs
 * @returns {Promise<object[]>} - Array of user profiles (those that exist)
 */
export async function getUserProfiles(userIds) {
  if (!userIds || userIds.length === 0) {
    return []
  }

  const profiles = await Promise.all(
    userIds.map(async (userId) => {
      const profile = await getUserProfile(userId)
      return profile
    })
  )

  return profiles.filter(Boolean)
}

/**
 * Update a user's display name
 * 
 * @param {string} userId - The user's UID
 * @param {string} displayName - The new display name
 * @returns {Promise<void>}
 */
export async function updateUserDisplayName(userId, displayName) {
  if (!userId) {
    throw new Error('User ID is required')
  }

  const userRef = doc(db, USERS_COLLECTION, userId)
  
  await setDoc(userRef, {
    displayName: displayName.trim(),
  }, { merge: true })
}
